import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface ProducerInvitation {
  invitation_id: string;
  producer_id: string;
  producer_name: string;
  producer_slug: string | null;
  producer_cover_image_url: string | null;
  role: string;
  status: string;
  invited_at: string;
  expires_at: string;
  invited_by_name: string | null;
}

interface AcceptResult {
  status: 'accepted' | 'already_member';
  producer_id: string;
  role: string;
  member_id?: string;
  public_profile_id?: string;
}

interface DeclineResult {
  status: 'declined';
  ok: boolean;
}

interface UseMyInvitationsResult {
  invitations: ProducerInvitation[];
  isLoading: boolean;
  error: Error | null;
  count: number;
  accept: (invitationId: string) => Promise<AcceptResult>;
  decline: (invitationId: string) => Promise<DeclineResult>;
  isAccepting: boolean;
  isDeclining: boolean;
  refetch: () => void;
}

export function useMyInvitations(): UseMyInvitationsResult {
  const { user, refreshRoles } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['my-invitations', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase.rpc('get_my_producer_invitations');

      if (error) throw error;

      return (data ?? []) as ProducerInvitation[];
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const acceptMutation = useMutation({
    mutationFn: async (invitationId: string): Promise<AcceptResult> => {
      const { data, error } = await supabase.rpc('accept_producer_invite_by_id', {
        p_invite_id: invitationId,
      });

      if (error) throw error;

      return data as unknown as AcceptResult;
    },
    onSuccess: async (result, invitationId) => {
      // CRITICAL FIX: Refresh roles first so isProducer becomes true
      // This ensures RouteGuard allows access to producer portal
      await refreshRoles();
      
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ['my-invitations'] });
      queryClient.invalidateQueries({ queryKey: ['my-producers'] });
      queryClient.invalidateQueries({ queryKey: ['producer-members'] });
      queryClient.invalidateQueries({ queryKey: ['producer'] });

      // Best-effort: notify admin via edge function
      if (result.status === 'accepted' && result.producer_id) {
        supabase.functions.invoke('notify-invite-accepted', {
          body: { invitation_id: invitationId, producer_id: result.producer_id },
        }).catch(() => { /* best-effort */ });
      }

      if (result.status === 'already_member') {
        toast({
          title: 'Du är redan medlem',
          description: 'Du är redan en del av detta team.',
        });
      } else {
        toast({
          title: 'Inbjudan accepterad!',
          description: 'Du har nu åtkomst till värdportalen.',
        });
      }
    },
    onError: (error: Error) => {
      console.error('Accept invitation error:', error);
      
      let title = 'Något gick fel';
      let description = error.message || 'Kunde inte acceptera inbjudan. Försök igen.';
      
      // Handle team_limit_reached: "team_limit_reached: max X members allowed (current: Y)"
      if (error.message.includes('team_limit_reached')) {
        title = 'Teamet är fullt';
        const match = error.message.match(/max\s*(\d+)\s*members.*current:\s*(\d+)/i);
        if (match) {
          description = `Gården har nått sin gräns (${match[2]}/${match[1]} medlemmar). Kontakta gårdsägaren.`;
        } else {
          description = 'Gården har nått sin medlemsgräns. Kontakta gårdsägaren för att frigöra platser.';
        }
      }
      
      toast({
        title,
        description,
        variant: 'destructive',
      });
    },
  });

  const declineMutation = useMutation({
    mutationFn: async (invitationId: string): Promise<DeclineResult> => {
      const { data, error } = await supabase.rpc('decline_producer_invite_by_id', {
        p_invite_id: invitationId,
      });

      if (error) throw error;

      const result = data as Record<string, unknown>;

      if (result?.error_code === 'INVITE_ALREADY_HANDLED') {
        toast({
          title: 'Redan hanterad',
          description: 'Inbjudan är redan hanterad.',
        });
        queryClient.invalidateQueries({ queryKey: ['my-invitations'] });
        return { status: 'declined', ok: false } as DeclineResult;
      }

      return result as unknown as DeclineResult;
    },
    onSuccess: (result) => {
      if (result.ok !== false) {
        queryClient.invalidateQueries({ queryKey: ['my-invitations'] });
        toast({
          title: 'Inbjudan avböjd',
          description: 'Du tackade nej till inbjudan.',
        });
      }
    },
    onError: (error: Error) => {
      console.error('Decline invitation error:', error);
      toast({
        title: 'Något gick fel',
        description: error.message || 'Kunde inte avböja inbjudan. Försök igen.',
        variant: 'destructive',
      });
    },
  });

  const actionableCount = (data ?? []).filter(
    (inv) => inv.status === 'invited'
  ).length;

  return {
    invitations: data ?? [],
    isLoading,
    error: error as Error | null,
    count: actionableCount,
    accept: acceptMutation.mutateAsync,
    decline: declineMutation.mutateAsync,
    isAccepting: acceptMutation.isPending,
    isDeclining: declineMutation.isPending,
    refetch,
  };
}
